/**
 * @file pricingCosting.js
 * @version 1.15.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-17
 * @modified 2026-11-03
 * @description The rules of the Pricing & Costing screen (Cam and Jade's
 *              Pricing & Costing Review, decisions picked 17 Oct 2026). Pure:
 *              services/pricingCostingService.js loads the facts, these decide.
 *
 *   One row = one order line with what it was bought on (D2): its PO-D (or
 *   Xero-made PO) line and its order's PO-H sit on the same row. A PO-D line
 *   bought for no order line is a cost-only row; a till draft's line awaiting
 *   pricing is a sale-only row.
 *
 *   COST (D4, Cam's order — "firmest first"):
 *     1. bill        the bill for this PO line, in the PO line's unit
 *     2. change      the posted price change in force on the delivery date,
 *                    + additives defined separately from the item (not one the
 *                    item's code or name already names)
 *     2a. contract   the cost on the contract covering the delivery (1.6.0)
 *     3. last_bill   the last bill from this supplier (or its group) for this
 *                    item, when dated in the delivery's month
 *     4. item        the item's cost, when not 0 (+ separate additives)
 *     5. current     the cost on the PO / order line now
 *     6. last_bill   from an earlier month — offered last, flagged "previous
 *                    month's bill"
 *   The first with a cost above 0 is proposed.
 *
 *   SALE (D5 — contract, last sold, then cost + usual margin):
 *     invoice (an invoice gone out — read only), contract / monthly / milk
 *     powder in force on the delivery date, a rep's reply (imported from their
 *     spreadsheet, D7), the last price sold to this customer for this recipe
 *     in the same pricing window (samePricingWindow), then the cost + this customer's usual margin
 *     on this range — proposed, never Ready ("nobody has agreed this price").
 *     The price already on the line is always offered; proposed only when
 *     nothing above is.
 *
 *   Client source; utils/pricingCosting.js on the server is GENERATED from it
 *   (node scripts/sync-shared-utils.js) — the screen and the server decide alike.
 *
 *   Each side is confirmed on its own (D3); nothing is written until a person
 *   confirms (D6); a confirmed side reopens under Check when what is stored
 *   moves, or a newer bill / price change disagrees (D8).
 *
 *   1.3.0 (Cam, 19 Oct 2026): a rep's returned spreadsheet is read with each
 *   row's own cells (customer, product, unit, quantity, orders and the cost it
 *   was asked at — readRepReplyRows), and each order line it names is checked
 *   for what has changed since it was asked (repReplyLineCheck): gone,
 *   invoiced, already priced, another customer or product, another cost.
 *
 *   1.4.0 (Cam, 20 Oct 2026): "shorten other notes to be clear but concise
 *   and include clear action user can choose to perform or not and the effect"
 *   — every flag reads "<what> — <what you can do> to <its effect>", in the
 *   style of his "No haulage bill — add it to include in cost price."
 *
 *   1.5.0 (Cam, 20 Oct 2026 — "Store till cost and sale price and history
 *   separate from Xero cost and sale price and history (keep both) and offer
 *   both"): a till draft's line shows the till's own cost on the day of the
 *   sale beside the Xero item cost (tillCostInfo — the margin is worked on the
 *   till cost when there is one, and says so), and its sale options take the
 *   till's price on that day (saleOptions `till`: before any discount, what
 *   was charged said beside it) — offered, proposed only when nothing else
 *   is. An order line of an Epos Now product shows the till's prices on its
 *   delivery date for reference (never proposed).
 *
 *   1.6.0 (Cam, 20 Oct 2026 — "what matters is what was in place, and what
 *   is now in place, that holds a potential price or cost for an order
 *   delivered on X date"): the contract a line takes is found whatever its
 *   state today (services/pricingCostingService contractFor); its cost is a
 *   cost option after a bill and the price change in force (costOptions
 *   `contract`); a contract covering the delivery with no price is said, never
 *   proposed (flagsFor `sale.contractInfo`). A rep's returned price that meets
 *   a price confirmed here, or a contract price in force that differs, is a
 *   question to answer before it is stored (repReplyConflict), no longer a
 *   reason to untick the line.
 *   1.7.0 (Cam, 21 Oct 2026): a split family's one PO line is one cost —
 *   confirmable from any of its orders, for all of them; the line says so
 *   (familyCostNote, an info flag).
 *   1.8.0 (Cam, 23 Oct 2026): haulage in the cost. A bill linked to the PO
 *   with a product line AND a haulage / delivery line: the haulage is shared
 *   over the bill's product lines for that PO (by tonnes where every weight
 *   is known, else by quantity — shareBillCharges) and added to the product's
 *   cost per unit ("£290.00 product + £12.50 haulage = £302.50" —
 *   billCostText); not when the PO carries Direct Delivery haulage for the
 *   product (that haulage is its own PO line's). A PO-H's haulier is paid by
 *   the PALLET: its bill is shared over the PO-H's lines by pallets
 *   (shareHaulageBill) and a line's haulage per unit is a pallet's charge
 *   over what a pallet holds (palletHolds — the line's qty per pallet, else
 *   the item's, else a tote line's 1 T; poHaulagePerUnit) — "£75.84 a pallet
 *   (up to 1.00 T) → £75.84/T" — never the bill ÷ a part pallet's quantity.
 *
 *   1.10.0 (2026-10-26, Cam — "costing adds the haulage and product bills'
 *   cost prices together which then informs users' sales price entry"): the
 *   cost + usual margin option is worked on the product's cost PLUS the
 *   line's haulage per unit (a PO-H haulier's bill share, or Direct Delivery
 *   haulage), as the margin shown already was — saleOptions({ costHaulage }),
 *   its detail "£400.00 product + £18.00 haulage = £418.00 + 12.0% …". The
 *   cost confirmed stays the product's (the PO-D is the product).
 *
 *   1.9.0 (2026-10-23, Cam): flagsFor notes a split order's tonnage added to
 *   the customer's other order — on the split line (sale.addedTo: "Added to
 *   SO-D… — invoiced there") and on the line taking it (sale.tonnage).
 *
 *   1.11.0 (2026-10-28, Cam — P7, the redesign's D11: a rep's flag is "a red
 *   flag on the line wherever it is"): an open rep flag (sale.repFlag — a
 *   rep's note for the office, or a confirmed line the rep pulled back) is an
 *   error flag on the line (repFlagText), so a line not yet confirmed goes to
 *   Check; an invoiced or confirmed line keeps its pile and carries the flag.
 *   The rep piles themselves are utils/repPricing.js.
 *
 *   1.12.0 (2026-10-28, Cam — N4: "Add the option to right click / three dots
 *   menus … in pricing and costing to 'Contract these prices' … Also allow
 *   reps to flag for standard users if they want contract generated from
 *   their prices"): contractPriceRows — the chosen order lines as the
 *   contract window's rows (one per customer and product, its price(s) and
 *   where each came from, the lines left out with why); readRepReplyRows
 *   reads the rep's "Make this a contract? (Y)" column (`contractAsked`; a
 *   sheet without it reads as before); repContractRequestText — a rep's
 *   request for a contract, in words for the office.
 *
 *   1.13.0 (2026-10-28, Cam — the redesign's D1, "Five stages in order"):
 *   stageOf / stageCounts — Needs a price → Check → Ready → Confirmed →
 *   Invoiced on the Lines view; a line whose sale is with a rep sits in Needs
 *   a price only while its cost is not set or confirmed, else it is counted
 *   "+ N costed, with reps" under it (Cam's note). Each flag says which side
 *   it is about (a third element, 'cost' | 'sale'), for the four-step strip
 *   (D2). The "asked N days ago" reminder flag and ASK_REMINDER_DAYS are gone
 *   (D5: no reminders).
 *   Prices per the ITEM's unit (a contract's price and cost, a monthly
 *   price, the mineral list) and a price last sold on a line in another unit
 *   come in the line's own unit, converted by the server and said
 *   (`unitNote` — "£400.00/T on the contract = £10.00/25kg bag"); one that
 *   cannot be converted is offered but never proposed (Cam's D7 note: "What
 *   we CANNOT have … is unit sale prices being mixed up").
 *
 *   1.14.0 (2026-10-29, N9 — the redesign's callout E, "options collapse when
 *   sources agree"): every option carries `short` — its source in a few words
 *   ("Bill FSIN50376235", "Price change 01/10", "Last bill … (previous
 *   month)") — so options giving the same figure can be shown as ONE, naming
 *   each source (utils/pricingCostingView.optionEntries); and `unitApart`
 *   when its figure is not per the line's own unit (a contract's / the
 *   mineral list's price that could not be converted, a price last sold in a
 *   unit that cannot be compared) — such an option never joins another.
 *   Which option is proposed does not change.
 *
 *   1.15.0 (2026-11-03, Cam — "Please add the option to include a note when
 *   hand typing a cost or sale price … Also please investigate why … a set
 *   cost or price can sometimes appear to just vanish"): a figure SET but not
 *   yet confirmed is kept on the server (pricing.line_choices) instead of only
 *   in the tab. cleanChoice is the one shape of a kept choice ({ id } — an
 *   option — or { id: 'manual', value, entered: { value, per } }); cleanNote
 *   the note a typed figure may carry (one line, at most NOTE_MAX = 200
 *   characters). historyGroups keeps the notes typed with the confirmed
 *   figures of a group's lines (`notes`, up to three).
 */

// The Summer / Winter pricing period (1 May – 30 Sep, 1 Oct – 30 Apr), as
// utils/pricingSeason.js on the server — inlined because that file is not
// shared with the screens; pricingCosting.test.js checks the two agree.
const dayOf = (v) => {
  if (!v) return null;
  if (v instanceof Date) return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(v);
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};
const seasonKey = (d) => {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  if (m >= 5 && m <= 9) return `S${y}`;
  return `W${m >= 10 ? y : y - 1}`;
};

const EPS = 0.00005;
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const same = (a, b) => a !== null && b !== null && Math.abs(Number(a) - Number(b)) < EPS;

// No reminder after N days (Cam, 28 Oct 2026, D5 — "no reminders"; chasing
// is done from Rep Pricing's Sends when he chooses). ASK_REMINDER_DAYS is gone.

/** Lines of a usual margin needed before it is offered as a price. */
export const USUAL_MARGIN_MIN_SAMPLES = 3;

export const COST_LABELS = {
  bill: 'Bill for this PO',
  change: 'Price change in force',
  contract: 'Contract cost',
  last_bill: 'Last bill from the supplier',
  item: 'Item cost',
  current: 'On the order now'
};

export const SALE_LABELS = {
  invoice: 'On the invoice',
  contract: 'Contract',
  monthly: 'Monthly price',
  milk_powder: 'Milk powder price',
  mineral: 'Mineral price list',
  rep: 'Rep reply',
  last_sold: 'Last sold to them',
  margin: 'Cost + usual margin',
  current: 'On the order now',
  till: 'Till price',
  manual: 'Typed by hand'
};

/** A pile a row sits in, in the screen's order. */
export const PILES = ['check', 'held', 'needs', 'ready', 'asked', 'done', 'invoiced'];
export const PILE_LABELS = {
  ready: 'Ready to confirm',
  check: 'Check',
  needs: 'Needs a price',
  asked: 'Asked a rep',
  held: 'Held rep prices',
  done: 'Confirmed',
  invoiced: 'Invoiced'
};

export const ukDate = (d) => {
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}` : '';
};
/** DD/MM/YYYY. */
export const ukDateFull = (d) => {
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '';
};
const monthOf = (d) => String(d || '').slice(0, 7);
export const money = (v) => {
  const n = num(v);
  if (n === null) return '—';
  const s = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `${n < 0 ? '−' : ''}£${s}`;
};

/**
 * The part of a line's additives defined separately from its item: an
 * additive whose code or name the item's own code or name already carries
 * ("Dairy Blend 18% + Mintrex" with Mintrex) is in the item's price, so it is
 * not added again (Cam, D4).
 * @param {Array<{ code?, name?, price? }>} additives
 */
export function separateAdditiveCost(additives, itemCode, itemName) {
  const hay = `${itemCode || ''} ${itemName || ''}`.toLowerCase();
  let total = 0;
  for (const a of additives || []) {
    const price = num(a && (a.price ?? a.additive_price ?? a.additivePrice));
    if (!price) continue;
    const code = String(a.code ?? a.additive_code ?? a.additiveCode ?? '').trim().toLowerCase();
    const name = String(a.name ?? a.additive_name ?? a.additiveName ?? '').trim().toLowerCase();
    const named = (code && code.length >= 2 && hay.includes(code)) || (name && name.length >= 3 && hay.includes(name));
    if (!named) total += price;
  }
  return round4(total);
}

const fmt2 = (n) => Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const fmtCount = (n) => Number(n).toLocaleString('en-GB', { maximumFractionDigits: 4 });

/**
 * A bill's haulage / delivery charges for a PO, shared over its product lines
 * for that PO (Cam, 23 Oct 2026): by tonnes when every product line's weight
 * is known, else by quantity; one product line takes it all.
 * @param {Array<{ qty, kg }>} products  kg = what one of the line's units weighs (null unknown)
 * @param {number} total                 the charges, net
 * @returns {{ shares: number[], by: 'one'|'tonnes'|'quantity'|'equal'|null }}
 */
export function shareBillCharges(products, total) {
  const list = Array.isArray(products) ? products : [];
  const t = num(total) || 0;
  if (!list.length) return { shares: [], by: null };
  if (!t) return { shares: list.map(() => 0), by: null };
  if (list.length === 1) return { shares: [round4(t)], by: 'one' };
  const tonnes = list.map(p => (num(p.kg) > 0 && num(p.qty) > 0 ? (num(p.qty) * num(p.kg)) / 1000 : null));
  const byTonnes = tonnes.every(x => x !== null);
  const basis = byTonnes ? tonnes : list.map(p => Math.abs(num(p.qty) || 0));
  const sum = basis.reduce((a, b) => a + b, 0);
  if (!(sum > 0)) return { shares: list.map(() => round4(t / list.length)), by: 'equal' };
  return { shares: basis.map(b => round4((t * b) / sum)), by: byTonnes ? 'tonnes' : 'quantity' };
}

/** "£290.00 product + £12.50 haulage = £302.50/T" — a bill's cost with its haulage. */
export function billCostText({ product, haulage, per = '' }) {
  const p = num(product);
  const h = num(haulage);
  if (p === null || h === null) return '';
  return `${money(p)} product + ${money(h)} haulage = ${money(round4(p + h))}${per || ''}`;
}

/**
 * A PO-H haulier's bill shared over the PO-H's lines (Cam, 23 Oct 2026 — the
 * haulier charges by the pallet): by pallets when any line has them, else by
 * weight, else equally.
 * @param {Array<{ pallets, weightKg }>} lines  the PO-H's haulage_lines
 * @param {number} billed                       the haulier's bill, net
 * @returns {{ shares: number[], by: 'pallets'|'weight'|'equal'|null }}
 */
export function shareHaulageBill(lines, billed) {
  const list = Array.isArray(lines) ? lines : [];
  const b = num(billed) || 0;
  if (!list.length || !(b > 0)) return { shares: list.map(() => null), by: null };
  const pallets = list.map(l => Math.max(0, num(l.pallets) || 0));
  const weights = list.map(l => Math.max(0, num(l.weightKg) || 0));
  const by = pallets.some(p => p > 0) ? 'pallets' : (weights.some(w => w > 0) ? 'weight' : 'equal');
  const basis = by === 'pallets' ? pallets : (by === 'weight' ? weights : list.map(() => 1));
  const sum = basis.reduce((a, c) => a + c, 0) || 1;
  return { shares: basis.map(x => round4((b * x) / sum)), by };
}

/**
 * What one pallet holds, in the line's own unit: the line's qty per pallet,
 * else the item's; else a line in totes (Cam, 23 Oct 2026: "up to 1T here,
 * i.e. 2 totes") — a tonne line 1 T, a 500kg tote line 2 totes; else not known.
 * @param {object} f
 * @param {number|null} f.lineQtyPerPallet
 * @param {number|null} f.itemQtyPerPallet
 * @param {number|null} f.lineKg   what one of the line's units weighs
 * @param {boolean} f.tote         the line is of a product in totes
 * @param {string} [f.unit]        'T' or the pack's name ('500kg tote')
 * @returns {{ holds: number|null, how: 'line'|'item'|'tote'|null, words: string }}
 */
export function palletHolds({ lineQtyPerPallet = null, itemQtyPerPallet = null, lineKg = null, tote = false, unit = '' } = {}) {
  const say = (n) => (unit === 'T' || !unit ? `${fmt2(n)} ${unit || ''}`.trim() : `${fmtCount(n)} × ${unit}`);
  if (num(lineQtyPerPallet) > 0) return { holds: num(lineQtyPerPallet), how: 'line', words: say(num(lineQtyPerPallet)) };
  if (num(itemQtyPerPallet) > 0) return { holds: num(itemQtyPerPallet), how: 'item', words: say(num(itemQtyPerPallet)) };
  const kg = num(lineKg);
  if (tote && kg > 0) {
    const holds = kg >= 1000 ? round4(1000 / kg) : Math.max(1, Math.floor(1000 / kg));
    return { holds, how: 'tote', words: say(holds) };
  }
  return { holds: null, how: null, words: '' };
}

/**
 * A line's PO-H haulage per unit: its share of the haulier's bill a pallet at
 * a time, over what a pallet holds — "£75.84 a pallet (up to 1.00 T) →
 * £75.84/T". What a pallet holds not known (or no pallets): the share over the
 * line's quantity, as before, and said.
 * @returns {{ per_unit: number|null, how: 'pallet'|'quantity'|null, words: string }}
 */
export function poHaulagePerUnit({ share, pallets, qty, holds = null, holdsWords = '', per = '' }) {
  const s = num(share);
  if (s === null) return { per_unit: null, how: null, words: '' };
  const pal = num(pallets);
  if (pal > 0 && num(holds) > 0) {
    const perPallet = round4(s / pal);
    const perUnit = round4(perPallet / num(holds));
    return {
      per_unit: perUnit, how: 'pallet',
      words: `${money(perPallet)} a pallet (up to ${holdsWords}) → ${money(perUnit)}${per}`
    };
  }
  const q = num(qty);
  if (!(q > 0)) return { per_unit: null, how: null, words: '' };
  const perUnit = round4(s / q);
  return {
    per_unit: perUnit, how: 'quantity',
    words: `${money(perUnit)}${per} — what a pallet holds isn't known, so worked on the quantity`
  };
}

/**
 * Cost options for one line, in Cam's order, and the one proposed.
 * Every value is per unit of the line, additives included (the PO line's
 * unitAmount basis). Facts not known are null and simply not offered.
 *
 * @param {object} f
 * @param {{ value, ref, date, at }|null} f.bill       bill for this PO line (PO line's unit)
 * @param {{ value, ref, date, at, effectiveDate }|null} f.change  price change cost (no additives)
 * @param {{ value, ref, date }|null} f.lastBill        last bill, supplier + item (PO line's unit)
 * @param {{ value, ref, detail }|null} [f.contract]    the cost on the contract covering the delivery
 * @param {number|null} f.itemCost                       item cost (no additives)
 * @param {string|null} f.itemCostDate
 * @param {number|null} f.current                        on the PO / order line now (additives included)
 * @param {string} f.currentLabel                        e.g. "On PO-D10498 now"
 * @param {number} f.separateAdditives                   from separateAdditiveCost
 * @param {string|null} f.deliveryDate                   'YYYY-MM-DD'
 * @returns {{ options: object[], proposed: object|null }}
 */
export function costOptions(f) {
  // A bill's reference may already say "Bill …" (1.14.0): its short name says it once.
  const billNo = (ref) => String(ref).replace(/^bill\s+/i, '');
  const options = [];
  const add = num(f.separateAdditives) || 0;
  const addNote = add ? ` · + additives ${money(add)}` : '';
  const sameMonth = f.lastBill && f.deliveryDate && monthOf(f.lastBill.date) === monthOf(f.deliveryDate);
  const lastBillOption = (old) => ({
    id: 'last_bill',
    label: `Last bill from ${f.supplierName || 'the supplier'}`,
    short: `Last bill${f.lastBill.ref ? ` ${billNo(f.lastBill.ref)}` : ''}${old ? ' (previous month)' : ''}`,
    detail: `${f.lastBill.ref || 'Bill'} · ${ukDate(f.lastBill.date)}${old ? ' · previous month\'s bill' : ''}`,
    value: round4(f.lastBill.value),
    ref: f.lastBill.ref || null,
    at: f.lastBill.at || null,
    firm: !old,
    oldMonth: !!old
  });

  if (f.bill && num(f.bill.value) !== null) {
    options.push({
      id: 'bill', label: COST_LABELS.bill, short: f.bill.ref ? `Bill ${billNo(f.bill.ref)}` : COST_LABELS.bill,
      detail: `${f.bill.ref || 'Bill'}${f.bill.date ? ` · ${ukDate(f.bill.date)}` : ''}${f.bill.note ? ` · ${f.bill.note}` : ''}`,
      value: round4(f.bill.value), ref: f.bill.ref || null, at: f.bill.at || null, firm: true
    });
  }
  if (f.change && num(f.change.value) !== null) {
    options.push({
      id: 'change',
      label: `Price change in force on ${ukDate(f.deliveryDate)}`,
      short: `Price change${ukDate(f.change.effectiveDate || f.change.date) ? ` ${ukDate(f.change.effectiveDate || f.change.date).slice(0, 5)}` : ''}`,
      detail: `effective ${ukDate(f.change.effectiveDate || f.change.date)}${addNote}`,
      value: round4(num(f.change.value) + add), ref: f.change.ref || null, at: f.change.at || null, firm: true
    });
  }
  // The contract's own cost (1.6.0, Cam 20 Oct 2026): the cost the contract
  // covering the delivery was agreed on — after a bill and the price change in
  // force (firmer), proposed when neither is there.
  if (f.contract && num(f.contract.value) > 0) {
    // Per the item's unit (1.13.0): converted to the line's by the server, said
    // in `unitNote`; one that cannot be converted is offered, never proposed.
    options.push({
      id: 'contract', label: `${COST_LABELS.contract}${f.contract.ref ? ` ${f.contract.ref}` : ''}`,
      short: f.contract.ref ? `Contract ${f.contract.ref}` : COST_LABELS.contract,
      detail: [f.contract.detail || '', f.contract.unitNote || ''].filter(Boolean).join(' · '),
      value: round4(f.contract.value), ref: f.contract.ref || null, at: null,
      firm: !f.contract.unconverted, ...(f.contract.unconverted ? { proposable: false, unitApart: true } : {})
    });
  }
  if (f.lastBill && num(f.lastBill.value) !== null && sameMonth) options.push(lastBillOption(false));
  if (num(f.itemCost) !== null && num(f.itemCost) !== 0) {
    options.push({
      id: 'item', label: COST_LABELS.item, short: COST_LABELS.item,
      detail: `${f.itemCostDate ? `updated ${ukDate(f.itemCostDate)}` : 'from the item'}${addNote}`,
      value: round4(num(f.itemCost) + add), ref: null, at: null, firm: false
    });
  }
  if (num(f.current) !== null) {
    options.push({
      id: 'current', label: f.currentLabel || COST_LABELS.current, short: f.currentLabel || COST_LABELS.current,
      detail: f.currentDetail || '',
      value: round4(f.current), ref: null, at: null, firm: false, isCurrent: true
    });
  }
  if (f.lastBill && num(f.lastBill.value) !== null && !sameMonth) options.push(lastBillOption(true));

  const proposed = options.find(o => num(o.value) > 0 && o.proposable !== false) || null;
  return { options, proposed };
}

/**
 * Sale options for one line and the one proposed.
 * @param {object} f
 * @param {{ value, ref, date }|null} f.invoice      a live invoice gone out — read only
 * @param {{ kind, value, ref, detail }|null} f.contract  in force on the delivery date
 * @param {{ value, date, notes, by }|null} f.rep    the newest rep reply
 * @param {{ value, ref, date, samePeriod, recipeMatch }|null} f.lastSold
 * @param {{ pc, n }|null} f.usualMargin             this customer's usual margin on this range
 * @param {number|null} f.cost                       the cost the margin is worked on (proposed / confirmed;
 *                                                   with costHaulage, product + haulage)
 * @param {{ product, haulage }|null} [f.costHaulage] the product's cost and the haulage per unit in `cost`
 * @param {number|null} f.current                    the price on the line now (0 = none)
 * @param {string} [f.currentLabel]
 * @param {number|null} [f.haulagePerUnit]           Direct Delivery haulage per unit, added to a
 *                                                   source whose terms were not Direct Delivery
 * @param {'till'|'xero'|null} [f.costBasis]          whose cost the margin is worked on (a till
 *                                                   draft's line: the till's, else the Xero item's)
 * @param {{ value, day, charged, how, reference }|null} [f.till]  the till's price ex VAT on the day
 *                                                   (how 'sale_line' = the sale's own line, before
 *                                                   any discount; else the till's price list).
 *                                                   Offered; proposed only when nothing else is,
 *                                                   never when `reference` (an order line)
 * @returns {{ options, proposed, locked }}
 */
export function saleOptions(f) {
  const options = [];
  if (f.invoice && num(f.invoice.value) !== null) {
    options.push({
      id: 'invoice', label: `On invoice ${f.invoice.ref || ''}`.trim(), short: `On invoice ${f.invoice.ref || ''}`.trim(),
      detail: 'invoice gone out: change it with a credit or debit note',
      value: round4(f.invoice.value), ref: f.invoice.ref || null, firm: true, locked: true
    });
    return { options, proposed: options[0], locked: true };
  }
  const haul = num(f.haulagePerUnit) || 0;
  const withHaulage = (value, terms) => {
    if (!haul || terms === 'Direct Delivery') return { value: round4(value), note: '' };
    return { value: round2(Number(value) + haul), note: ` · + haulage ${money(haul)}` };
  };
  if (f.contract && num(f.contract.value) > 0) {
    const h = withHaulage(f.contract.value, f.contract.terms);
    const contractLabel = f.contract.ref ? `${SALE_LABELS[f.contract.kind] || 'Contract'} ${f.contract.ref}` : (SALE_LABELS[f.contract.kind] || 'Contract');
    options.push({
      id: f.contract.kind || 'contract',
      label: contractLabel, short: contractLabel,
      detail: [`${f.contract.detail || ''}${h.note}`, f.contract.unitNote || ''].filter(Boolean).join(' · '),
      value: h.value, ref: f.contract.ref || null, firm: !f.contract.unconverted,
      // Per the item's unit and not convertible to the line's (1.13.0): offered, never proposed.
      ...(f.contract.unconverted ? { proposable: false, unitApart: true } : {}),
      // Which contract / month / milk powder price (28 Oct 2026): a sale
      // confirmed from it links the order line to it.
      targetId: f.contract.id != null ? Number(f.contract.id) : null
    });
  }
  if (f.rep && num(f.rep.value) > 0) {
    // A held rep price (Cam, 19 Oct) is offered but never proposed: it waits
    // in Held rep prices until it is approved or checked again with the rep.
    const held = f.rep.status === 'held';
    options.push({
      id: 'rep', label: `Rep reply${held ? ' (held)' : ''}${f.rep.by ? ` (${f.rep.by})` : ''}`,
      short: `Rep reply${held ? ' (held)' : ''}${f.rep.by ? ` (${f.rep.by})` : ''}`,
      detail: `imported ${ukDate(f.rep.date)}${f.rep.notes ? ` · "${String(f.rep.notes).slice(0, 80)}"` : ''}`,
      value: round4(f.rep.value), ref: null, firm: !held, proposable: !held, held
    });
  }
  // The Mineral Pricing list (pricing.mineral_pricing — cost + haulage +
  // margin, priced per item; Cam, 20 Oct 2026): a mineral's own list price,
  // proposed after a contract or a rep's reply. Haulage is already in it.
  // The breakdown (a cost) only for those who see costs.
  if (f.mineral && num(f.mineral.value) > 0) {
    const m = f.mineral;
    // The price is the item's cost NOW + the list's haulage and margin (the
    // view reads inventory.items' cost live), so each date says what it is:
    // the cost's (items.cost_price_updated_at) and the margin's (the list
    // row's updated_at) — Cam, 21 Oct 2026, read "updated 09/04" as the price
    // being stale when only the margin was.
    const parts = [];
    if (num(m.cost) !== null) parts.push(`cost ${money(m.cost)}${m.costUpdated ? ` (item cost from ${ukDate(m.costUpdated)})` : ' (item cost now)'}`);
    if (num(m.haulage)) parts.push(`haulage ${money(m.haulage)}`);
    if (num(m.margin)) parts.push(`margin ${money(m.margin)}`);
    const marginWhen = m.updated ? `margin set ${ukDate(m.updated)}` : 'from Mineral Pricing';
    const listed = parts.length
      ? `${parts.join(' + ')} · ${marginWhen}`
      : `today's item cost + haulage + margin · ${marginWhen}`;
    options.push({
      id: 'mineral', label: SALE_LABELS.mineral, short: SALE_LABELS.mineral,
      detail: [listed, m.unitNote || ''].filter(Boolean).join(' · '),
      value: round4(m.value), ref: null, firm: !m.unconverted,
      ...(m.unconverted ? { proposable: false, unitApart: true } : {})
    });
  }
  if (f.lastSold && num(f.lastSold.value) > 0) {
    const h = withHaulage(f.lastSold.value, f.lastSold.terms);
    const notes = [];
    if (!f.lastSold.recipeMatch) notes.push('a different recipe');
    if (!f.lastSold.samePeriod) notes.push('an earlier pricing period');
    // Sold in another unit (1.13.0): converted by the server when both units
    // are known (said in `unitNote`); one that cannot be compared
    // (`unitClash` — None against a weight) is offered, never proposed.
    const sameUnit = !f.lastSold.unitClash;
    options.push({
      id: 'last_sold', label: SALE_LABELS.last_sold,
      short: `Last sold${f.lastSold.ref ? ` ${f.lastSold.ref}` : ''}${notes.length ? ` (${notes.join(', ')})` : ''}`,
      detail: `${f.lastSold.ref || ''} · ${ukDate(f.lastSold.date)}${notes.length ? ` · ${notes.join(', ')}` : ''}${h.note}${f.lastSold.unitNote ? ` · ${f.lastSold.unitNote}` : ''}`,
      value: h.value, ref: f.lastSold.ref || null,
      firm: !!(f.lastSold.samePeriod && f.lastSold.recipeMatch && sameUnit),
      proposable: !!(f.lastSold.samePeriod && f.lastSold.recipeMatch && sameUnit),
      ...(sameUnit ? {} : { unitApart: true }),
      // The earlier sale itself, for its cost and margin (Pricing & Costing's
      // earlier-sales read, 1.13.0).
      soldOn: f.lastSold.date || null
    });
  }
  if (f.usualMargin && num(f.cost) > 0) {
    const basis = f.costBasis === 'till' ? ' till cost' : f.costBasis === 'xero' ? ' Xero item cost' : '';
    // Product + haulage (1.10.0): both said, so the figure is never a mystery.
    const ch = f.costHaulage && num(f.costHaulage.haulage) ? f.costHaulage : null;
    const costWords = ch
      ? `${money(ch.product)} product + ${money(ch.haulage)} haulage = ${money(f.cost)}`
      : `${money(f.cost)}${basis}`;
    options.push({
      id: 'margin', label: SALE_LABELS.margin, short: SALE_LABELS.margin,
      detail: `${costWords} + ${f.usualMargin.pc.toFixed(1)}% (their usual on this range, ${f.usualMargin.n} lines)`,
      value: round2(num(f.cost) * (1 + f.usualMargin.pc / 100)), ref: null, firm: false
    });
  }
  const till = tillSaleOption(f.till);
  if (till) options.push(till);
  if (num(f.current) > 0) {
    options.push({
      id: 'current', label: f.currentLabel || SALE_LABELS.current, short: f.currentLabel || SALE_LABELS.current,
      detail: f.currentDetail || '',
      value: round4(f.current), ref: null, firm: false, isCurrent: true
    });
  }
  const proposed = options.find(o => o.id !== 'current' && o.proposable !== false && num(o.value) > 0)
    || options.find(o => o.id === 'current')
    // The till's price only when nothing else is — and never on an order line.
    || (till && !till.reference ? till : null)
    || null;
  return { options, proposed, locked: false };
}

/**
 * The till's price as a sale option (null when there is none).
 * @param {{ value, day, charged, how, reference }|null} t
 */
export function tillSaleOption(t) {
  if (!t || !(num(t.value) > 0)) return null;
  const when = ukDateFull(t.day);
  let detail;
  if (t.reference) detail = 'Epos Now price list, ex VAT — for reference';
  else if (t.how === 'sale_line') {
    detail = num(t.charged) > 0 && !same(t.charged, t.value)
      ? `Epos Now sale, ex VAT before discount · charged ${money(t.charged)} after a discount`
      : 'Epos Now sale, ex VAT';
  } else detail = 'Epos Now price list on the day of the sale, ex VAT';
  return {
    id: 'till', label: `${SALE_LABELS.till}${when ? ` on ${when}` : ''}`,
    short: `${SALE_LABELS.till}${when ? ` ${when.slice(0, 5)}` : ''}`,
    detail, value: round4(t.value), ref: 'Epos Now', firm: false, proposable: false,
    reference: !!t.reference
  };
}

/**
 * A till draft line's costs, shown beside its sale (a till line has no cost to
 * confirm): the till's own cost on the day of the sale (Epos Now) and the Xero
 * item cost — both, labelled. The margin is worked on the till cost when there
 * is one, else the Xero item cost.
 * @param {{ till: { cost, day, how }|null, itemCost: number|null, itemCostDate: string|null }} f
 * @returns {{ info: { value, text }|null, lines: Array<{ id, label, detail, value }>,
 *            marginCost: number|null, basis: 'till'|'xero'|null, note: string|null }}
 */
export function tillCostInfo(f) {
  const lines = [];
  const tc = f.till && num(f.till.cost) > 0 ? round4(f.till.cost) : null;
  const xc = num(f.itemCost) !== null && num(f.itemCost) !== 0 ? round4(f.itemCost) : null;
  if (tc !== null) {
    lines.push({
      id: 'till', label: `Till cost ${money(tc)}${f.till.day ? ` on ${ukDateFull(f.till.day)}` : ''} (Epos Now)`,
      detail: f.till.how === 'sale_line' ? 'ex VAT, as the till recorded it at the sale' : 'ex VAT, the till\'s price list that day',
      value: tc
    });
  }
  if (xc !== null) {
    lines.push({
      id: 'xero_item', label: `Xero item cost ${money(xc)}${f.itemCostDate ? ` (updated ${ukDateFull(f.itemCostDate)})` : ''}`,
      detail: 'ex VAT, the item\'s cost in Xero now', value: xc
    });
  }
  const basis = tc !== null ? 'till' : xc !== null ? 'xero' : null;
  const marginCost = tc !== null ? tc : xc;
  const note = basis === 'till' ? 'Margin worked on the till cost.'
    : basis === 'xero' ? 'No till cost — margin worked on the Xero item cost.' : null;
  return {
    info: lines.length ? { value: lines[0].value, text: lines[0].label } : null,
    lines, marginCost, basis, note
  };
}

/**
 * A confirmed side, reopened (D8) when what the line holds has moved since,
 * or a bill / price change newer than the confirmation disagrees with it.
 * @param {{ value, confirmed_at }} conf
 * @param {number|null} stored  what the line holds now
 * @param {object|null} proposed  the proposal worked out now
 * @returns {string|null} the reason, or null when it stands
 */
export function reopenReason(conf, stored, proposed, side = 'cost') {
  if (!conf) return null;
  const v = num(conf.value);
  const word = side === 'cost' ? 'cost' : 'price';
  if (num(stored) !== null && !same(stored, v)) {
    return `changed on the line since it was confirmed (${money(v)} → ${money(stored)})`;
  }
  if (proposed && (proposed.id === 'bill' || proposed.id === 'change') && proposed.at && conf.confirmed_at
    && new Date(proposed.at).getTime() > new Date(conf.confirmed_at).getTime()
    && !same(proposed.value, v)) {
    const diff = num(proposed.value) - v;
    const what = proposed.id === 'bill' ? (proposed.ref || 'a bill') : 'a price change';
    return `${what} is ${money(Math.abs(diff))} ${diff > 0 ? 'above' : 'below'} the confirmed ${word}`;
  }
  return null;
}

/**
 * Flags on a line — each [kind, text], kind 'err' | 'warn' | 'info'. Anything
 * but info sends the line to Check.
 * @param {object} r  { cost: { proposed, current, applies, xeroMade, oldMonth... },
 *                      sale: { proposed, current, applies, locked, asked },
 *                      haulage, today }
 */
export function flagsFor(r) {
  const f = [];
  const c = r.cost || {};
  const s = r.sale || {};
  const cv = c.chosen ? num(c.chosen.value) : null;
  const sv = s.chosen ? num(s.chosen.value) : null;

  if (c.applies && !c.confirmed) {
    if (!c.chosen) f.push(['err', 'No cost found — type one to cost the line.', 'cost']);
    else if (cv === 0) f.push(['err', 'Cost is £0 — choose another option or type one.', 'cost']);
    else {
      if (c.chosen.id !== 'current' && num(c.current) !== null && num(c.current) > 0 && !same(c.current, cv)) {
        const d = cv - num(c.current);
        f.push(['warn', `${c.chosen.id === 'bill' ? 'Bill' : c.chosen.label} ${money(Math.abs(d))}${r.per || ''} ${d > 0 ? 'above' : 'below'} ${c.currentWhere || 'the line'} (${money(c.current)}) — confirm to update it.`, 'cost']);
      }
      if (c.chosen.oldMonth) {
        const when = String(c.chosen.detail || '').split(' · ')[1] || '';
        f.push(['warn', `Cost from an earlier month's bill${when ? ` (${when})` : ''} — confirm to use it, or choose another.`, 'cost']);
      }
      if (c.chosen.id === 'manual') f.push(['warn', 'Cost typed by hand — check it, then confirm.', 'cost']);
      if (c.chosen.id === 'current' && !c.firmAvailable) f.push(['info', "No bill, price change or this month's bill yet — confirm to keep the line's cost.", 'cost']);
    }
    if (c.xeroMade) f.push(['info', 'PO made in Xero — confirming sends the cost to Xero too.', 'cost']);
    if (c.block) f.push(['info', `${c.block}.`, 'cost']);
    if (c.billed && c.chosen && !same(c.current, cv)) f.push(['warn', `${c.poNumber || 'The PO'} is billed — confirming changes its cost anyway.`, 'cost']);
  }
  if (c.reopened) f.push(['warn', `Cost reopened: ${c.reopened} — confirm again to keep it, or choose another.`, 'cost']);
  // A split family's one PO line (Cam, 21 Oct 2026): one cost for every order on it.
  if (c.applies && c.family) f.push(['info', familyCostNote(c.family, !!c.confirmed), 'cost']);

  if (s.applies && !s.confirmed && !s.locked) {
    if (s.chosen) {
      if (sv !== null && sv <= 0) f.push(['err', 'Price is £0 — choose another option or type one.', 'sale']);
      if (s.chosen.id === 'margin') f.push(['warn', 'Cost + usual margin, not agreed — confirm to use it, or ask the rep.', 'sale']);
      if (s.chosen.id === 'manual') f.push(['warn', 'Price typed by hand — check it, then confirm.', 'sale']);
      if (s.chosen.id !== 'current' && num(s.current) > 0 && !same(s.current, sv)) {
        const d = sv - num(s.current);
        f.push(['warn', `${s.chosen.label} ${money(Math.abs(d))}${r.per || ''} ${d > 0 ? 'above' : 'below'} the order's price (${money(s.current)}) — confirm to update the order.`, 'sale']);
      }
    }
    // No "asked N days ago" reminder on the line (Cam, 28 Oct 2026, D5: no
    // reminders — chasing is done from Rep Pricing's Sends when he chooses).
    // The Sale cell says who has it and since when.
  }
  if (s.reopened) f.push(['warn', `Price reopened: ${s.reopened} — confirm again to keep it, or choose another.`, 'sale']);
  // A contract covering the delivery with no price (1.6.0) — information only.
  if (s.contractInfo && !s.locked) f.push(['info', s.contractInfo, 'sale']);
  // A split order's tonnage added to the customer's other order (Cam, 23 Oct
  // 2026): this line is invoiced there, or the other way round.
  if (s.addedTo) f.push(['info', `${s.addedTo.note}.`, 'sale']);
  else if (s.locked) f.push(['info', 'Invoiced — the sale price is read only; edit the invoice to change it.', 'sale']);
  if (s.tonnage && s.tonnage.note) f.push(['info', s.tonnage.note, 'sale']);
  // A rep's flag for the office (1.11.0): red, on the line wherever it is.
  if (s.repFlag) f.push(['err', repFlagText(s.repFlag), 'sale']);

  const cm = r.marginCost;
  if (cm !== null && cm !== undefined && sv !== null && sv > 0 && num(cm) > 0 && sv < num(cm)) {
    f.unshift(['err', `Negative margin (${money(sv - num(cm))}${r.per || ''}) — raise the price or check the cost.`, 'sale']);
  }
  if (r.haulage && r.haulage.pending) f.push(['info', r.haulage.pending, 'cost']);
  return f;
}

/**
 * A rep's open flag on a line, in words (the office's hover text and the
 * line's flag): "Flagged by Matt Jackson on 27/10/26: "Customer agreed £320 …"
 * — mark it dealt with in Rep Pricing." A pull-back says the rep wants to
 * price a confirmed line again.
 * @param {{ kind, note, by, rep_name, at }} flag
 */
export function repFlagText(flag) {
  if (!flag) return '';
  const who = flag.by || flag.rep_name || 'A rep';
  const day = typeof flag.at === 'string' && flag.at.length > 10 ? dayOf(new Date(flag.at)) : dayOf(flag.at);
  const when = day ? ` on ${ukDate(day)}` : '';
  if (flag.kind === 'pull_back') {
    return `${who} pulled this back to price again${when}${flag.note ? `: "${flag.note}"` : ''} — mark it dealt with in Rep Pricing.`;
  }
  return `Flagged by ${who}${when}: "${flag.note || ''}" — mark it dealt with in Rep Pricing.`;
}

/**
 * The note on a split family's cost: "Cost shared with SO-D10234A (split) —
 * one line on PO-D10236, confirmed for both".
 * @param {{ others: string[], po: string|null, confirmed_on?: string|null }} family
 */
export function familyCostNote(family, confirmed) {
  const others = (family && family.others) || [];
  const names = others.length <= 1 ? others.join('') : `${others.slice(0, -1).join(', ')} and ${others[others.length - 1]}`;
  const all = others.length === 1 ? 'both' : `all ${others.length + 1}`;
  const where = family && family.po ? ` — one line on ${family.po}` : '';
  return `Cost shared with ${names || 'its split orders'} (split)${where}, ${confirmed ? `confirmed for ${all}` : `confirming sets it for ${all}`}.`;
}

/**
 * The pile a row sits in. Invoiced once its order has a live invoice (Cam, 19
 * Oct 2026 — invoiced from the Confirmed pile) unless a confirmed cost has
 * reopened (Check). Otherwise Needs / Asked first (no price to propose), then
 * Check (a flag that is not info, or a side reopened), then Ready, and
 * Confirmed once every side that applies is confirmed.
 */
export function pileOf(r) {
  const c = r.cost || {};
  const s = r.sale || {};
  if (s.locked) return c.applies && c.confirmed && c.reopened ? 'check' : 'invoiced';
  const costDone = !c.applies || (c.confirmed && !c.reopened);
  const saleDone = !s.applies || s.locked || (s.confirmed && !s.reopened);
  if (costDone && saleDone) return 'done';
  // A rep price held for review (Cam, 19 Oct) — until approved or re-asked.
  if (s.applies && !saleDone && s.repHeld) return 'held';
  if (s.applies && !saleDone && !s.chosen) return s.asked ? 'asked' : 'needs';
  if ((r.flags || []).some(([k]) => k !== 'info')) return 'check';
  return 'ready';
}

/**
 * THE FIVE STAGES (Cam, 28 Oct 2026 — the redesign's D1, "Five stages in
 * order"): Needs a price → Check → Ready → Confirmed → Invoiced, plus To do
 * and All. Rep work (asking, waiting, held prices) is Rep Pricing's; a line's
 * pile (pileOf) is unchanged for it — the stage is how the Lines view reads it.
 *
 * Cam's note on Needs a price: "Make the 'Needs a Price' pile not display
 * orders where the price is with the rep (so long as cost is set or
 * confirmed), but do display those that are with the rep if the cost is not
 * set/ confirmed. Then the '+ # with reps' count would become '+ # costed,
 * with reps' and would count those with reps for a sale price where cost is
 * set/ confirmed, and this would be sat under the main count which is those
 * that need a cost and are with reps as well as those needing a cost and/or
 * price that are not with reps."
 *
 *   needs      a side still to set has nothing to propose (no cost, or no sale
 *              price) and the sale is not with a rep; or the sale is with a
 *              rep and the cost is not set or confirmed
 *   with_reps  the sale is with a rep and the cost is set or confirmed — in no
 *              stage of its own: To do and All, and "+ N costed, with reps"
 *              under Needs a price
 *   check      something proposed differs or needs a look (pileOf 'check')
 *   ready      every side proposed, nothing to look at (pileOf 'ready')
 *   done       every side that applies confirmed — Confirmed
 *   invoiced   its order's invoice is raised — Invoiced
 */
export const STAGES = ['needs', 'check', 'ready', 'done', 'invoiced'];
export const STAGE_LABELS = {
  todo: 'To do',
  needs: 'Needs a price',
  with_reps: 'Costed, with reps',
  check: 'Check',
  ready: 'Ready',
  done: 'Confirmed',
  invoiced: 'Invoiced',
  all: 'All'
};

/** Is a side done: not applying, invoiced (sale), or confirmed and not reopened. */
const sideDone = (x) => !x || !x.applies || !!x.locked || (!!x.confirmed && !x.reopened);

/** Does a side that is still to set have a figure above £0 on screen? */
const sideSet = (x) => sideDone(x) || (!!x.chosen && num(x.chosen.value) > 0);

/** The cost is set (a figure above £0 proposed or chosen) or confirmed — or there is no cost here. */
export function costSet(r) {
  return sideSet((r || {}).cost);
}

/**
 * Is the line's sale price with a rep: still to set, nothing proposed, and
 * the rep asked (waiting) — or the rep's price is held for review.
 */
export function saleWithRep(r) {
  const s = (r || {}).sale || {};
  if (!s.applies || sideDone(s)) return false;
  if (s.repHeld) return true;
  return !!s.asked && !s.chosen;
}

/**
 * The stage a line reads in on the Lines view.
 * @param {{ cost, sale, flags, pile }} r  a row (pile from pileOf)
 */
export function stageOf(r) {
  const pile = r.pile || pileOf(r);
  if (pile === 'invoiced') return 'invoiced';
  if (pile === 'done') return 'done';
  if (saleWithRep(r)) return costSet(r) ? 'with_reps' : 'needs';
  const c = r.cost || {};
  const s = r.sale || {};
  const needsCost = !!c.applies && !sideDone(c) && !(c.chosen && num(c.chosen.value) > 0);
  const needsSale = !!s.applies && !sideDone(s) && !s.chosen;
  if (needsCost || needsSale) return 'needs';
  return pile === 'ready' ? 'ready' : 'check';
}

/**
 * The stage counts of some rows (each carrying `stage`, else worked out):
 * { todo, needs, with_reps, check, ready, done, invoiced, all }. To do is
 * every line not confirmed — costed lines with reps included.
 */
export function stageCounts(rows) {
  const c = { todo: 0, needs: 0, with_reps: 0, check: 0, ready: 0, done: 0, invoiced: 0, all: 0 };
  for (const r of rows || []) {
    const st = r.stage || stageOf(r);
    c[st] = (c[st] || 0) + 1;
    c.all += 1;
  }
  c.todo = c.all - c.done - c.invoiced;
  return c;
}

/**
 * This customer's usual margin on a range: the median markup on cost over
 * their priced lines, when there are enough of them.
 * @param {Array<{ price, cost }>} samples
 * @returns {{ pc: number, n: number }|null}
 */
export function usualMargin(samples) {
  const pcs = (samples || [])
    .map(s => ({ p: num(s.price), c: num(s.cost) }))
    .filter(s => s.p > 0 && s.c > 0)
    .map(s => (s.p - s.c) / s.c * 100)
    .filter(pc => pc > -50 && pc < 200)
    .sort((a, b) => a - b);
  if (pcs.length < USUAL_MARGIN_MIN_SAMPLES) return null;
  const mid = Math.floor(pcs.length / 2);
  const median = pcs.length % 2 ? pcs[mid] : (pcs[mid - 1] + pcs[mid]) / 2;
  return { pc: Math.round(median * 10) / 10, n: pcs.length };
}

/**
 * Twelve months of cost, sale and margin for a customer and product, each
 * distinct combination once with how many lines had it (Cam, D5 note:
 * "unique only with count"). Newest first. 1.13.0: a sale's unit is part of
 * it (a price per bag and one per tonne are never one), and each group keeps
 * where its cost came from (the bills / POs, up to three) and its delivery
 * dates.
 * 1.15.0: the notes typed with each line's confirmed figures go with its
 * group (`notes` [{ side, note, by, at, ref }], up to three, each once).
 * @param {Array<{ date, cost, sale, ref, unit?, cost_ref?, cost_from?, cost_note?, notes? }>} rows
 */
export function historyGroups(rows) {
  const by = new Map();
  for (const r of rows || []) {
    const cost = num(r.cost) === null ? null : round4(r.cost);
    const sale = num(r.sale) === null ? null : round4(r.sale);
    const unit = r.unit || null;
    const key = `${cost}|${sale}|${unit || ''}`;
    const g = by.get(key) || { cost, sale, unit, count: 0, first: null, last: null, refs: [], cost_refs: [], cost_notes: [], dates: [], notes: [] };
    g.count += 1;
    const d = String(r.date || '').slice(0, 10);
    if (d && (!g.last || d > g.last)) g.last = d;
    if (d && (!g.first || d < g.first)) g.first = d;
    if (d && g.dates.length < 6 && !g.dates.includes(d)) g.dates.push(d);
    if (r.ref && g.refs.length < 5 && !g.refs.includes(r.ref)) g.refs.push(r.ref);
    if (r.cost_ref && g.cost_refs.length < 3 && !g.cost_refs.includes(r.cost_ref)) g.cost_refs.push(r.cost_ref);
    if (r.cost_note && g.cost_notes.length < 2 && !g.cost_notes.includes(r.cost_note)) g.cost_notes.push(r.cost_note);
    // The notes typed with the figures confirmed on these lines (1.15.0).
    for (const n of Array.isArray(r.notes) ? r.notes : []) {
      if (!n || !n.note || g.notes.length >= 3) continue;
      if (g.notes.some(x => x.side === n.side && x.note === n.note)) continue;
      g.notes.push({ side: n.side === 'cost' ? 'cost' : 'sale', note: String(n.note), by: n.by || null, at: n.at || null, ref: r.ref || null });
    }
    by.set(key, g);
  }
  return [...by.values()]
    .map(g => ({
      ...g,
      dates: [...g.dates].sort().reverse(),
      margin: g.cost !== null && g.sale !== null ? round4(g.sale - g.cost) : null,
      margin_pc: g.cost > 0 && g.sale !== null ? Math.round((g.sale - g.cost) / g.cost * 1000) / 10 : null
    }))
    .sort((a, b) => String(b.last || '').localeCompare(String(a.last || '')));
}

/**
 * Are two days in the same pricing window for this range? The window the
 * system already judges a cost fresh in (utils/lineCostSql): the week for milk
 * powder (200P), the quarter for minerals (206), the month for blends (202 /
 * 207A); otherwise the Summer / Winter pricing period (compounds' contract
 * periods are Summer and Winter too).
 */
export function samePricingWindow(a, b, rangeCode) {
  const da = dayOf(a);
  const db = dayOf(b);
  if (!da || !db) return false;
  const code = String(rangeCode || '').toUpperCase();
  if (code === '200P') {
    const monday = (d) => {
      const t = new Date(`${d}T12:00:00Z`);
      t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7));
      return t.toISOString().slice(0, 10);
    };
    return monday(da) === monday(db);
  }
  if (code === '206') {
    const q = (d) => `${d.slice(0, 4)}Q${Math.floor((Number(d.slice(5, 7)) - 1) / 3)}`;
    return q(da) === q(db);
  }
  if (code === '202' || code === '207A') return da.slice(0, 7) === db.slice(0, 7);
  return seasonKey(da) === seasonKey(db);
}

/**
 * The product categories the screen filters by (Cam, 19 Oct 2026), from the
 * item's sales account code: Milk Powder 200P / 205, Compounds (Bags) 207,
 * Blends (Bags) 207A, Compounds (Bulk) 201, Blends (Bulk) 202, Minerals 206,
 * Fats 209, everything else Other.
 */
export const PRODUCT_CATEGORIES = [
  { key: 'milk_powder', label: 'Milk Powder', codes: ['200P', '205'] },
  { key: 'compounds_bags', label: 'Compounds (Bags)', codes: ['207'] },
  { key: 'blends_bags', label: 'Blends (Bags)', codes: ['207A'] },
  { key: 'compounds_bulk', label: 'Compounds (Bulk)', codes: ['201'] },
  { key: 'blends_bulk', label: 'Blends (Bulk)', codes: ['202'] },
  { key: 'minerals', label: 'Minerals', codes: ['206'] },
  { key: 'fats', label: 'Fats', codes: ['209'] },
  { key: 'other', label: 'Other', codes: [] }
];

/** A sales account code's product category key ('other' when none fits). */
export function productCategoryOf(salesAccountCode) {
  const code = String(salesAccountCode || '').trim().toUpperCase();
  const hit = PRODUCT_CATEGORIES.find(c => c.codes.includes(code));
  return hit ? hit.key : 'other';
}

/**
 * "ol:12" | "po:45:li:abc" | "il:9" | "xp:45:li:abc" — a row's key and what
 * it names. "xp:" is a line of a PO made in Xero (PO-…), priced and invoiced
 * per line (Cam, 19 Oct 2026 — the Xero POs pile).
 */
export function parseLineKey(key) {
  const s = String(key || '');
  let m = /^xp:(\d+):(.+)$/.exec(s);
  if (m) return { kind: 'xero_po_line', purchaseOrderId: Number(m[1]), poLineKey: m[2] };
  m = /^ol:(\d+)$/.exec(s);
  if (m) return { kind: 'order_line', orderLineId: Number(m[1]) };
  m = /^il:(\d+)$/.exec(s);
  if (m) return { kind: 'invoice_line', invoiceLineId: Number(m[1]) };
  m = /^po:(\d+):(.+)$/.exec(s);
  if (m) return { kind: 'po_line', purchaseOrderId: Number(m[1]), poLineKey: m[2] };
  return null;
}

// ---------------------------------------------------------------------------
// Figures set but not yet confirmed, and the note on a typed one (1.15.0)
// ---------------------------------------------------------------------------

/** The longest note a typed cost or price may carry. */
export const NOTE_MAX = 200;
const CHOICE_PERS = ['unit', 't', 'kg'];
const shortText = (v, max) => {
  if (v === null || v === undefined) return '';
  const t = String(v).trim();
  return t.length > max ? null : t;
};

/**
 * A note typed with a hand-typed figure: one line (newlines and runs of
 * spaces become one space), trimmed; '' when there is none. Longer than
 * NOTE_MAX → null (refused, never cut short).
 */
export function cleanNote(v) {
  if (v === null || v === undefined) return '';
  const t = String(v).replace(/\s+/g, ' ').trim();
  return t.length > NOTE_MAX ? null : t;
}

/**
 * A kept choice, as the screen holds it and the server stores it: an option
 * ({ id }) or a typed figure ({ id: 'manual', value, entered?: { value,
 * per } }) — the figure per the line's own unit and as typed. Anything else
 * on the object is dropped (the note travels apart). Null when it is not a
 * choice (no id, an id that is not a word, figures that are not figures).
 * A typed choice with no figure yet keeps value '' (isBlankChoice): the
 * server keeps it only when it carries a note.
 */
export function cleanChoice(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  if (!/^[a-z_]{1,40}$/i.test(id)) return null;
  if (id !== 'manual') return { id };
  const value = shortText(raw.value, 24);
  if (value === null || !/^\d*\.?\d*$/.test(value) || value === '.') return null;
  const out = { id, value };
  if (raw.entered && typeof raw.entered === 'object') {
    const ev = shortText(raw.entered.value, 24);
    const per = CHOICE_PERS.includes(raw.entered.per) ? raw.entered.per : 'unit';
    if (ev !== null && /^\d*\.?\d*$/.test(ev)) out.entered = { value: ev, per };
  }
  return out;
}

/** A typed choice with no figure in it (yet). */
export function isBlankChoice(c) {
  return !!c && c.id === 'manual' && (c.value === '' || c.value === null || c.value === undefined);
}

/**
 * The rows a rep's filled-in price request spreadsheet carries: each with the
 * order line ids of its hidden Ref column, the price typed and the notes.
 * Columns are found by their header (row `headerRow`), never by letter — the
 * cost columns come and go with the sender's permission.
 * @param {Array<Array<any>>} sheetRows  rows as arrays of cell values (1-based rows → index 0 = row 1)
 * @param {{ headerRow?: number, refHeader?: string }} [opts]
 * @returns {{ rows: Array<{ row, lineIds, price, notes, priceText }>, problems: string[] }}
 */
export function readRepReplyRows(sheetRows, { headerRow = 3, refHeader = 'Ref (do not change)' } = {}) {
  const problems = [];
  const header = (sheetRows[headerRow - 1] || []).map(v => String(cellText(v)).trim().toLowerCase());
  const col = (pred) => header.findIndex(pred);
  const refCol = col(h => h === refHeader.toLowerCase());
  const priceCol = col(h => h.startsWith('your price'));
  const notesCol = col(h => h === 'notes');
  // The row's own cells (1.3.0): what the rep was asked, shown back for checking.
  const cells = {
    accountNo: col(h => h === 'acc #'),
    customer: col(h => h === 'customer'),
    itemCode: col(h => h === 'item code'),
    product: col(h => h.startsWith('product')),
    unit: col(h => h === 'unit'),
    quantity: col(h => h === 'quantity'),
    orders: col(h => h.startsWith('orders')),
    cost: col(h => h.startsWith('current cost'))
  };
  // N4: "Make this a contract? (Y)" — sheets sent before 28 Oct 2026 have none.
  const contractCol = col(h => h.startsWith('make this a contract'));
  if (refCol < 0 || priceCol < 0) {
    problems.push('This is not a price request spreadsheet from the system — it has no "Your price" or Ref column on row 3.');
    return { rows: [], problems };
  }
  const rows = [];
  for (let i = headerRow; i < sheetRows.length; i++) {
    const r = sheetRows[i] || [];
    const ref = String(cellText(r[refCol]) || '').trim();
    if (!ref) continue;
    const lineIds = ref.split(/[,\s]+/).map(x => Number(x)).filter(n => Number.isInteger(n) && n > 0);
    const priceText = String(cellText(r[priceCol]) ?? '').trim();
    const price = parsePrice(r[priceCol]);
    const notes = notesCol >= 0 ? String(cellText(r[notesCol]) || '').trim() : '';
    if (!lineIds.length) { problems.push(`Row ${i + 1}: the Ref cell was changed, so it can't be matched.`); continue; }
    if (priceText && price === null) problems.push(`Row ${i + 1}: "${priceText}" is not a price.`);
    const cell = (k) => (cells[k] >= 0 ? String(cellText(r[cells[k]]) ?? '').trim() : '');
    const costText = cell('cost');
    const costs = costRangeOf(costText);
    rows.push({
      row: i + 1, lineIds, price, notes, priceText,
      accountNo: cell('accountNo'), customer: cell('customer'), itemCode: cell('itemCode'), product: cell('product'),
      unit: cell('unit'), quantity: cell('quantity'), orders: cell('orders'),
      // The cost the request was asked at — only when the sheet carried costs.
      costText, costMin: costs ? costs.min : null, costMax: costs ? costs.max : null, hasCostColumn: cells.cost >= 0,
      // The rep asks for their price to be made a contract price (N4).
      contractAsked: contractCol >= 0 && contractAnswerIsYes(r[contractCol])
    });
  }
  return { rows, problems };
}

/** A "Make this a contract?" cell the rep filled in as yes: Y, Yes, X, ✓, True, 1. */
export function contractAnswerIsYes(v) {
  if (v === true) return true;
  const t = String(cellText(v) ?? '').trim().toLowerCase();
  return ['y', 'yes', 'x', '✓', '✔', 'true', '1', 'yes please'].includes(t);
}

function cellText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    if (v.result !== undefined) return v.result;
    if (Array.isArray(v.richText)) return v.richText.map(t => t.text).join('');
    if (v.text !== undefined) return v.text;
  }
  return v;
}

/** "300.00" → { min: 300, max: 300 }; "290.00–300.00" → { 290, 300 }; else null. */
export function costRangeOf(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  const parts = t.replace(/(\d)\s*-\s*(\d)/, '$1–$2').split(/\s*[–—]\s*/).map(parsePrice);
  if (!parts.length || parts.some(p => p === null) || parts.length > 2) return null;
  return { min: Math.min(...parts), max: Math.max(...parts) };
}

const normCode = (v) => String(v || '').trim().toLowerCase();

/**
 * Has this order line changed since the rep was asked about it? (Cam, 19 Oct
 * 2026: "lines that have changed since the request … flagged and unticked by
 * default with the reason".)
 * @param {object} sheet    the spreadsheet's row: { accountNo, itemCode, costMin, costMax }
 * @param {object} line     the line now: { missing, invoiced, sale_confirmed: { value, by }|null,
 *                          account_no, customer, product_code }
 * @param {number|null} costNow  the cost the line carries now (as the screen shows it), when known
 * @returns {{ ok: boolean, reasons: string[], notes: string[] }}  ok = nothing changed; reasons
 *          say what did (the line starts unticked); notes are said without unticking it
 */
export function repReplyLineCheck(sheet, line, costNow = null) {
  const reasons = [];
  const notes = [];
  if (!line || line.missing) return { ok: false, reasons: ['No longer on any order'], notes };
  if (line.invoiced) reasons.push(`Invoiced since (${line.invoiced})`);
  // A sale already confirmed here is no longer a reason to leave the line out
  // (1.6.0): it is a question — keep it, or use the rep's (repReplyConflict).
  if (line.sale_confirmed && num(line.sale_confirmed.value) !== null && same(num(line.sale_confirmed.value), num(sheet?.price))) {
    notes.push(`Already confirmed at ${money(line.sale_confirmed.value)} — the rep's price`);
  }
  const acc = String(sheet?.accountNo || '').trim();
  if (acc && line.account_no && acc.toLowerCase() !== String(line.account_no).trim().toLowerCase()) {
    reasons.push(`Now for ${line.customer || 'another customer'} (${line.account_no}) — the sheet asked for ${acc}`);
  }
  const code = normCode(sheet?.itemCode);
  if (code && normCode(line.product_code) && code !== normCode(line.product_code)) {
    reasons.push(`The product is now ${line.product_code} — the sheet asked about ${sheet.itemCode}`);
  }
  const lo = num(sheet?.costMin);
  const hi = num(sheet?.costMax);
  if (lo !== null && hi !== null) {
    const now = num(costNow);
    if (now === null) notes.push('Its cost now could not be checked');
    else if (now < lo - EPS || now > hi + EPS) {
      reasons.push(`The cost has changed since the request: ${lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`} then, ${money(now)} now`);
    }
  }
  return { ok: reasons.length === 0, reasons, notes };
}

/** A typed price — 12.5, "£12.50", "12.50/T", "£1,234.50" — or null. */
export function parsePrice(v) {
  const t = cellText(v);
  if (typeof t === 'number') return Number.isFinite(t) && t > 0 ? round4(t) : null;
  const s = String(t || '').replace(/[£,\s]/g, '').replace(/\/.*$/, '');
  if (!s) return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? round4(n) : null;
}


// ---------------------------------------------------------------------------
// Rep replies against what the line already has (1.6.0, Cam 20 Oct 2026)
// ---------------------------------------------------------------------------

/** What each answer to a rep-reply question is called, everywhere. */
export const REP_CONFLICT_LABELS = {
  keep: 'Keep confirmed',
  contract: 'Use contract',
  rep: "Use the rep's"
};

/** "Contract EXA123", "typed", "rep reply" — where a confirmed price came from. */
export function confirmedSourceText(c) {
  if (!c) return '';
  const src = String(c.source || '').trim();
  if (!src || src === 'manual') return 'typed';
  if (src === 'rep') return 'rep reply';
  const label = SALE_LABELS[src] || src.replace(/_/g, ' ');
  return `${label}${c.source_ref ? ` ${c.source_ref}` : ''}`;
}

/** "Confirmed £300.00 (Contract EXA123 — by Jade Hill, 14/10)". */
export function confirmedText(c) {
  if (!c || num(c.value) === null) return '';
  const when = c.at ? ukDate(c.at).slice(0, 5) : '';
  const by = [c.by ? `by ${c.by}` : '', when].filter(Boolean).join(', ');
  return `Confirmed ${money(c.value)} (${confirmedSourceText(c)}${by ? ` — ${by}` : ''})`;
}

/**
 * Does a rep's price for a line meet a price already settled? (Cam, 20 Oct
 * 2026 — ask which to keep before anything is stored.) A question when the
 * line's sale price is CONFIRMED here at another figure, or a contract price
 * in force on its delivery date differs from the rep's.
 *
 * @param {{ sale_confirmed?: { value, source, source_ref, by, at }|null,
 *           contract_now?: { value, ref, kind }|null }} line
 * @param {number} repPrice
 * @param {{ contractWillBe?: number|null }} [o]  the row is being made a contract
 *        price at the rep's (Part C): the contract will then be that figure
 * @returns {null|{ confirmed, contract, choices: string[], key: string }}
 */
export function repReplyConflict(line, repPrice, o = {}) {
  const rep = num(repPrice);
  if (!line || !(rep > 0)) return null;
  const confirmed = line.sale_confirmed && num(line.sale_confirmed.value) > 0 ? line.sale_confirmed : null;
  const cv = o.contractWillBe !== undefined && o.contractWillBe !== null ? num(o.contractWillBe)
    : (line.contract_now && num(line.contract_now.value) > 0 ? num(line.contract_now.value) : null);
  const contract = cv > 0 ? { ...(line.contract_now || {}), value: cv } : null;
  const confirmedDiffers = !!(confirmed && !same(num(confirmed.value), rep));
  const contractDiffers = !!(contract && !same(cv, rep));
  if (!confirmedDiffers && !contractDiffers) return null;
  const choices = [];
  if (confirmed) choices.push('keep');
  if (contractDiffers && !(confirmed && same(num(confirmed.value), cv))) choices.push('contract');
  if (!confirmed || confirmedDiffers) choices.push('rep');
  return {
    confirmed, contract: contractDiffers ? contract : null, choices,
    key: `${confirmed ? round4(confirmed.value) : ''}|${contractDiffers ? round4(cv) : ''}`
  };
}

/**
 * The answer a bulk "Keep every confirmed price" / "Use every rep price" gives
 * a question, or null when it does not apply to it. "The rep's" on a line
 * already confirmed at the rep's figure is keeping it.
 */
export function bulkConflictAnswer(conflict, want) {
  if (!conflict) return null;
  if (want === 'keep') return conflict.choices.includes('keep') ? 'keep' : null;
  if (want === 'rep') return conflict.choices.includes('rep') ? 'rep' : (conflict.choices.includes('keep') ? 'keep' : null);
  return conflict.choices.includes(want) ? want : null;
}

/**
 * Which contract tab a product's contract belongs on (the contract screens'
 * own split — client/src/utils/accountCodes), or why a rep's price for it
 * cannot be made a contract price from here.
 * @returns {{ id: number, name: string }|{ refused: string }}
 */
export function contractCategoryFor(salesAccountCode) {
  const c = String(salesAccountCode || '').trim().toUpperCase();
  if (!c) return { refused: 'The product has no sales account — set it on the item first' };
  if (c === '201') return { id: 3, name: 'compounds' };
  if (c === '202') return { id: 2, name: 'blends' };
  if (c === '206') return { id: 4, name: 'minerals' };
  if (['207', '207A', '200N'].includes(c)) return { id: 7, name: 'bags' };
  if (c === '204') return { refused: 'Straights contracts are priced by month — make it on the Contracts › Straights tab' };
  if (c === '200P') return { refused: 'Milk powder prices are set on the Milk Powder screen' };
  if (c === '210') return { refused: 'Additives are priced on the Additives screen, not as a contract' };
  return { id: 8, name: 'other' };
}

/** What a contract a rep's price can join holds, in a sentence: "PR-11742 · Winter 2026-27 · 2 products". */
export function contractGroupText(g) {
  if (!g) return '';
  const name = g.contract_number || g.supplier_numbers?.[0] || `Contract #${g.ids?.[0] ?? ''}`;
  const when = g.period_name || `${ukDate(g.start_date)} – ${ukDate(g.end_date)}`;
  const state = [g.on_hold ? 'on hold' : '', g.archived ? 'archived' : ''].filter(Boolean).join(', ');
  const n = (g.products || []).length;
  return `${name} · ${when} · ${n} product${n === 1 ? '' : 's'}${state ? ` · ${state}` : ''}`;
}

/** What may be done with another product of a contract a rep's price joins. */
export const CONTRACT_OTHER_ACTIONS = {
  keep: 'Keep as it is',
  hold: 'Put on hold',
  archive: 'Archive',
  replace: 'Replace'
};

/** "tonne", "25kg bag" — a unit as said in a sentence. */
const unitWords = (u) => (u === 'T' ? 'tonne' : (u || ''));

/**
 * "Contract these prices" (N4 — Cam, 28 Oct 2026): the order lines chosen in
 * Pricing & Costing as the contract window's rows, the way the rep-reply
 * import's Part C takes them — one row per customer and product (a contract
 * row is one product), its price as the screen shows it, and the lines that
 * cannot be made a contract price left out with why. Pure.
 *
 * @param {Array<object>} lines  each line's facts (the server's contract line
 *        facts) with `price` (the sale price on screen) and `from` (where it
 *        came from: "Confirmed", "Chosen: Last sold", "Proposed: Contract …")
 * @returns {{ rows: Array<{ row, key, lineIds, price, prices, customer, accountNo,
 *            itemCode, product, unit, unitNote, lines }>, leftOut: Array<object> }}
 *   `prices` [{ value, line_ids, from }] most lines first (the default price);
 *   lines at another price are still the row's — they deliver within its dates.
 */
export function contractPriceRows(lines) {
  const leftOut = [];
  const groups = new Map();
  for (const l of lines || []) {
    if (!l) continue;
    const why = l.missing ? 'No longer on any order'
      : l.invoiced ? `Invoiced (${l.invoiced}) — its price is on the invoice`
        : !(num(l.price) > 0) ? 'No sale price on the screen — choose or type one first'
          : !l.inventory_item_id ? 'No product from inventory — choose one on the order first'
            : (contractCategoryFor(l.sales_account_code).refused || null);
    if (why) { leftOut.push({ ...l, why }); continue; }
    const key = `${l.account_id}|${l.inventory_item_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(l);
  }
  const confirmed = (e) => e.from.some(f => /^confirmed/i.test(f));
  const rows = [...groups.entries()].map(([key, list]) => {
    list.sort((a, b) => String(a.delivery_date || '').localeCompare(String(b.delivery_date || '')) || (Number(a.id) - Number(b.id)));
    const byPrice = new Map();
    list.forEach((l, i) => {
      const p = round4(num(l.price));
      if (!byPrice.has(p)) byPrice.set(p, { value: p, line_ids: [], from: [], first: i });
      const e = byPrice.get(p);
      e.line_ids.push(l.id);
      if (l.from && !e.from.includes(l.from)) e.from.push(l.from);
    });
    const prices = [...byPrice.values()]
      .sort((a, b) => (b.line_ids.length - a.line_ids.length) || (Number(confirmed(b)) - Number(confirmed(a))) || (a.first - b.first))
      .map(({ first, ...e }) => e);
    const f = list[0];
    const itemUnit = f.item_unit || null;
    const other = list.filter(l => l.own_unit && itemUnit && l.own_unit !== itemUnit);
    const unitNote = other.length
      ? `A contract price is per the item's unit (${unitWords(itemUnit)}); ${[...new Set(other.map(l => l.order_number))].join(', ')} ${other.length === 1 ? 'is' : 'are'} in ${[...new Set(other.map(l => unitWords(l.own_unit)))].join(' / ')} — check the price is per ${unitWords(itemUnit)}.`
      : null;
    return {
      key, lineIds: list.map(l => l.id), price: prices[0].value, prices,
      customer: f.account_name || f.customer || '', accountNo: f.account_no || '',
      itemCode: f.item_code || f.product_code || '', product: f.item_name || f.product || '',
      unit: itemUnit || f.unit || '', unitNote, lines: list
    };
  }).sort((a, b) => {
    const an = a.accountNo || '';
    const bn = b.accountNo || '';
    if (!an !== !bn) return an ? -1 : 1;
    if (an !== bn) return an.localeCompare(bn, undefined, { numeric: true });
    return String(a.customer).localeCompare(String(b.customer)) || String(a.itemCode).localeCompare(String(b.itemCode));
  });
  rows.forEach((r, i) => { r.row = i + 1; });
  return { rows, leftOut };
}

/**
 * A rep's request for a contract, in words for the office (the line's hover
 * text and the contract window): "Matt Jackson asked for a contract at
 * £330.00 on 28/10/26 (their spreadsheet): "for the winter" — Contract these
 * prices, or Not now."
 * @param {{ rep_name, by, at, price, note, source }} req
 */
export function repContractRequestText(req, { action = true } = {}) {
  if (!req) return '';
  const who = req.rep_name || req.by || 'A rep';
  const day = typeof req.at === 'string' && req.at.length > 10 ? dayOf(new Date(req.at)) : dayOf(req.at);
  const at = num(req.price) > 0 ? ` at ${money(req.price)}` : '';
  const how = req.source === 'import' ? ' (their spreadsheet)' : req.source === 'rep_view' ? ' (their pricing screen)' : '';
  const note = req.note ? `: "${req.note}"` : '';
  return `${who} asked for a contract${at}${day ? ` on ${ukDate(day)}` : ''}${how}${note}${action ? ' — Contract these prices, or Not now.' : ''}`;
}
